import { AlertTriangle, Loader2 } from "lucide-react";

/**
 * Renders authored article HTML.
 *
 * The HTML is inserted directly, which is only safe because it was sanitised
 * server-side on write against an allowlist that matches the editor's feature
 * set exactly. Nothing outside that allowlist can be in the stored value —
 * see functions/lib/sanitize.js.
 *
 * Typography lives in the `prose-editorial` class in index.css rather than in
 * utility classes here, because the markup is generated and cannot carry them.
 */
export default function RichContent({ html, className = "" }) {
  if (!html) return null;
  return (
    <div
      className={`prose-editorial ${className}`}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

/** Shared loading state for public pages. */
export function SectionLoading({ label = "Loading" }) {
  return (
    <div className="py-24 flex flex-col items-center justify-center gap-3" role="status">
      <Loader2 className="h-5 w-5 text-[#cca24d] animate-spin" strokeWidth={1.5} />
      <span className="text-xs text-[#6b7280] tracking-widest uppercase">{label}</span>
    </div>
  );
}

/**
 * Shared error state.
 *
 * Deliberately does not blame the visitor or expose the underlying error. The
 * scope calls for graceful degradation when the API is unavailable, and an
 * alternative route to contact PIRAC is the useful part of that.
 */
export function SectionError({ onRetry, message }) {
  return (
    <div className="py-20 px-6">
      <div className="mx-auto max-w-lg text-center border border-[#1a2940] bg-[#0c1728] p-10">
        <AlertTriangle className="h-6 w-6 text-[#cca24d]/60 mx-auto mb-4" strokeWidth={1} />
        <h2 className="font-serif text-2xl text-[#f7f5f0]">This section is unavailable</h2>
        <p className="mt-3 text-sm text-[#9ca3af] leading-relaxed">
          {message || "We could not load this content just now. Please try again in a moment."}
        </p>
        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="h-11 px-6 bg-[#cca24d] text-[#060f1e] text-sm hover:bg-[#e5c98b] transition-colors"
            >
              Try again
            </button>
          )}
          <a
            href="/contact"
            className="h-11 px-6 inline-flex items-center border border-[#1a2940] text-sm text-[#d8d8d8] hover:border-[#cca24d]/50 transition-colors"
          >
            Contact us instead
          </a>
        </div>
      </div>
    </div>
  );
}

/** Shared empty state for filtered listings. */
export function SectionEmpty({ title, description, action }) {
  return (
    <div className="col-span-full py-20 text-center">
      <div className="font-serif text-2xl text-[#f7f5f0]">{title}</div>
      {description && (
        <p className="mt-3 text-sm text-[#9ca3af] max-w-md mx-auto leading-relaxed">{description}</p>
      )}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}
